import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { JackpotUpdateEvent, JackpotWonEvent } from '@tg/shared';
import { useSocketEvent } from '@/socket/socket';
import { useSession } from '@/stores/session';
import { useFormat } from '@/i18n/useFormat';
import { fx, sound, useCelebration, wait } from '@/fx';
import { gameName } from '@/features/achievements/text';
import { useJackpotStore } from './store';
import s from './jackpot.module.css';

/** the celebration closes on its own after the count-up and this pause */
export const JACKPOT_CELEBRATION_MS = 6000;

/**
 * Keeps the jackpot live (`jackpot:update`, `jackpot:won`) and celebrates the player's own win. Somebody else's win
 * is only a quiet line in the winners list (no sound, no coins). Honest play: one tap (or Esc / Space / Enter)
 * skips and closes the celebration; sound only when the player switched it on; reduced motion — no particles,
 * the amount appears at once.
 */
export function JackpotListener() {
  const me = useSession((st) => st.me?.publicId ?? null);
  const mine = useJackpotStore((st) => st.mine);
  useSocketEvent<[JackpotUpdateEvent]>('jackpot:update', (e) => useJackpotStore.getState().setPool(e.pool));
  useSocketEvent<[JackpotWonEvent]>('jackpot:won', (e) => useJackpotStore.getState().won(e, me !== null && e.publicId === me));
  if (!mine) return null;
  return <JackpotCelebration key={`${mine.at}:${mine.amount}`} e={mine} onDone={() => useJackpotStore.getState().dismiss()} />;
}

export function JackpotCelebration({ e, onDone }: { e: JackpotWonEvent; onDone(): void }) {
  const intl = useIntl();
  const f = useFormat();
  const cel = useCelebration();
  const box = useRef<HTMLDivElement>(null);
  const amount = useRef<HTMLSpanElement>(null);
  const done = useRef(onDone);
  done.current = onDone;
  const { run } = cel;

  useEffect(() => {
    let live = true;
    void run(async (signal) => {
      sound.play('common/mega');
      // the house celebration 乐游: gold and the brand colours, above any game's mega
      void fx.celebrate('jackpot', 'jackpot', box.current, { signal });
      await fx.countUp(amount.current, 0, e.amount, 2600, { signal, ticks: true, format: (n) => f.tokens(Math.round(n), { decimals: 0 }) });
      await wait(JACKPOT_CELEBRATION_MS, signal);
    }).then(() => {
      if (live) done.current();
    });
    return () => {
      live = false;
    };
    // one celebration per win (the key of the component changes with it)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={s.celebrate} role="dialog" aria-modal="true" aria-label={intl.formatMessage({ id: 'jackpot.won.title' })} data-testid="jackpot-celebration">
      <div ref={box} className={s.celebrateBox}>
        <span className={s.celebrateBrand}>
          <FormattedMessage id="jackpot.title" />
        </span>
        <span className={s.celebrateTitle}>
          <FormattedMessage id="jackpot.won.title" />
        </span>
        <span ref={amount} className={s.celebrateAmount}>
          {f.tokens(e.amount, { decimals: 0 })}
        </span>
        <span className={s.celebrateGame}>{gameName(intl, e.game)}</span>
        <span className={s.celebrateHint}>
          <FormattedMessage id="jackpot.won.skip" />
        </span>
      </div>
    </div>
  );
}
